import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useI18n } from '@/locales';
import { cn } from '@/lib/cn';
import { parseInvite, roomPath } from './invite';

export function JoinRoomForm({
  className,
  autoFocus,
}: {
  className?: string;
  autoFocus?: boolean;
}) {
  const { m } = useI18n();
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [invalid, setInvalid] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const invite = parseInvite(value);
    if (!invite) return setInvalid(true);
    void navigate(roomPath(invite.roomId, invite.inviteToken));
  };

  return (
    <form onSubmit={submit} className={cn('w-full', className)} noValidate>
      <label htmlFor="quick-join" className="sr-only">
        {m.landing.quickJoinLabel}
      </label>
      <div className="flex gap-2 rounded-xl bg-ink-850/80 p-1.5 ring-1 ring-line-strong focus-within:ring-2 focus-within:ring-volt-500/60">
        <Input
          id="quick-join"
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setInvalid(false);
          }}
          placeholder={m.landing.quickJoinPlaceholder}
          aria-invalid={invalid}
          aria-describedby={invalid ? 'quick-join-error' : undefined}
          autoComplete="off"
          spellCheck={false}
          className="h-10 bg-transparent ring-0 focus:ring-0"
        />
        <Button
          type="submit"
          variant="secondary"
          size="md"
          className="h-10"
          disabled={!value.trim()}
        >
          {m.common.join} <ArrowRight />
        </Button>
      </div>
      {invalid ? (
        <p id="quick-join-error" role="alert" className="mt-2 text-sm text-danger">
          {m.landing.quickJoinInvalid}
        </p>
      ) : null}
    </form>
  );
}
